import { ArrowLeft } from 'lucide-react';
import { useCallback, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useLocation, useNavigate, useSearchParams } from 'react-router-dom';

import { DeviceCheck } from '@/components/video/DeviceCheck';
import { HowItWorks } from '@/components/video/HowItWorks';
import { PromptRecorder } from '@/components/video/PromptRecorder';
import { UploadProgress, type UploadItem } from '@/components/video/UploadProgress';
import { VideoConsent } from '@/components/video/VideoConsent';
import { uploadVideoChunked, videoApi, type Prompt } from '@/services/videoService';
import { useApplicationStore } from '@/store/applicationStore';

type VideoStep = 'consent' | 'device-check' | 'how-it-works' | 'recording' | 'uploading' | 'already-recorded';

const TOTAL_STEPS = 5;

const STEP_NUMBER: Record<VideoStep, number> = {
  'consent': 1,
  'device-check': 2,
  'how-it-works': 3,
  'recording': 4,
  'uploading': 5,
  'already-recorded': 3,
};

// Fallback prompts — used when API is unavailable
const MOCK_PROMPTS: Prompt[] = [
  {
    promptId: 'p1',
    text: 'Tell us about yourself and why you are interested in a security officer position.',
    maxDurationSec: 120,
    order: 1,
  },
  {
    promptId: 'p2',
    text: 'Describe a situation where you had to handle a conflict or difficult person. How did you resolve it?',
    maxDurationSec: 120,
    order: 2,
  },
  {
    promptId: 'p3',
    text: 'What does professionalism mean to you in a security role?',
    maxDurationSec: 120,
    order: 3,
  },
];

const CHUNKED_UPLOAD_THRESHOLD = 5 * 1024 * 1024; // 5MB

function uploadBlobToS3(uploadUrl: string, blob: Blob, onProgress: (percent: number) => void): Promise<void> {
  return new Promise((resolve, reject) => {
    const xhr = new XMLHttpRequest();
    xhr.upload.onprogress = (e) => {
      if (e.lengthComputable) {
        onProgress(Math.round((e.loaded / e.total) * 100));
      }
    };
    xhr.onload = () => {
      if (xhr.status >= 200 && xhr.status < 300) {
        resolve();
      } else {
        reject(new Error('Upload failed'));
      }
    };
    xhr.onerror = () => reject(new Error('Upload failed'));
    xhr.onabort = () => reject(new Error('Upload cancelled'));
    xhr.open('PUT', uploadUrl);
    xhr.setRequestHeader('Content-Type', blob.type || 'video/webm');
    xhr.send(blob);
  });
}

export function VideoInterviewPage() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams] = useSearchParams();
  const serverApplicationId = useApplicationStore((s) => s.serverApplicationId);
  const routeStateApplicationId = (location.state as { applicationId?: string } | null)?.applicationId;
  const applicationId = searchParams.get('applicationId') ?? routeStateApplicationId ?? serverApplicationId;
  const dashboardUrl = applicationId ? `/dashboard?applicationId=${encodeURIComponent(applicationId)}` : '/dashboard';

  const [step, setStep] = useState<VideoStep>('consent');

  // Prompt set state
  const [prompts, setPrompts] = useState<Prompt[]>(MOCK_PROMPTS);
  const [retakePolicy, setRetakePolicy] = useState<'unlimited' | 'once' | 'none'>('once');

  // Interview state
  const [interviewId, setInterviewId] = useState<string | null>(null);
  const [interviewSubmittedAt, setInterviewSubmittedAt] = useState<string | null>(null);
  const [interviewResponseCount, setInterviewResponseCount] = useState(0);
  const [reRecordError, setReRecordError] = useState<string | null>(null);

  // Upload state
  const [uploadItems, setUploadItems] = useState<UploadItem[]>([]);
  const [uploadIndex, setUploadIndex] = useState(0);
  const [uploadPercent, setUploadPercent] = useState(0);
  const [uploadPhase, setUploadPhase] = useState<'uploading' | 'confirming' | 'submitting' | 'done' | 'error'>(
    'uploading',
  );
  const [uploadError, setUploadError] = useState<string | null>(null);

  const recordingsRef = useRef<Array<{ promptId: string; blob: Blob; durationSec: number }>>([]);

  // Fetch active prompt set on mount
  useEffect(() => {
    const fetchPromptSet = async () => {
      try {
        const response = await videoApi.getActivePromptSet();
        const promptSet = response.data;
        if (promptSet) {
          setPrompts(promptSet.prompts);
          setRetakePolicy(promptSet.retakePolicy);
        }
      } catch {
        // Fall back to MOCK_PROMPTS (already set as default)
      }
    };
    void fetchPromptSet();
  }, []);

  const handleBack = useCallback(() => {
    void navigate(dashboardUrl);
  }, [navigate, dashboardUrl]);

  const handleConsentAccepted = useCallback(() => {
    setStep('device-check');
  }, []);

  const handleDeviceReady = useCallback(() => {
    setStep('how-it-works');
  }, []);

  const handleStartRecording = useCallback(() => {
    if (!applicationId) {
      window.alert(t('video.upload.error'));
      return;
    }

    const start = async () => {
      try {
        const response = await videoApi.startInterview({
          applicationId,
        });
        if (response.data.promptSet) {
          setPrompts(response.data.promptSet.prompts);
          setRetakePolicy(response.data.promptSet.retakePolicy);
        }
        setInterviewId(response.data.videoInterviewId);
        setInterviewSubmittedAt(response.data.submittedAt ?? null);
        setInterviewResponseCount(response.data.responses.length);
        setReRecordError(null);
        if (response.data.status === 'uploaded') {
          setStep('already-recorded');
        } else {
          setStep('recording');
        }
      } catch (error) {
        const message = error instanceof Error ? error.message : t('video.upload.error');
        window.alert(message);
      }
    };

    void start();
  }, [applicationId, t]);

  const runUpload = useCallback(
    async (
      recordings: Array<{ promptId: string; blob: Blob; durationSec: number }>,
      startIndex: number,
      currentInterviewId: string,
    ) => {
      setUploadPhase('uploading');

      for (let i = startIndex; i < recordings.length; i++) {
        const recording = recordings[i]!;
        setUploadIndex(i);
        setUploadPercent(0);

        // Update item status to uploading
        setUploadItems((prev) =>
          prev.map((item, idx) => (idx === i ? { ...item, status: 'uploading' as const } : item)),
        );

        try {
          let fileKey: string;
          const isLargeFile = recording.blob.size >= CHUNKED_UPLOAD_THRESHOLD;

          if (isLargeFile) {
            // Chunked multipart upload for large files
            fileKey = await uploadVideoChunked(currentInterviewId, recording.blob, (percent) => {
              setUploadPercent(percent);
            });
          } else {
            // Single presigned URL upload for small files
            const urlResponse = await videoApi.getUploadUrl(currentInterviewId, {
              promptId: recording.promptId,
              contentType: 'video/webm',
            });
            fileKey = urlResponse.data.fileKey;

            await uploadBlobToS3(urlResponse.data.uploadUrl, recording.blob, (percent) => {
              setUploadPercent(percent);
            });
          }

          // Update item status to confirming
          setUploadItems((prev) =>
            prev.map((item, idx) => (idx === i ? { ...item, status: 'confirming' as const } : item)),
          );
          setUploadPhase('confirming');

          // Confirm upload
          await videoApi.confirmResponse(currentInterviewId, {
            promptId: recording.promptId,
            fileKey,
            durationSec: recording.durationSec,
          });

          // Mark as done
          setUploadItems((prev) => prev.map((item, idx) => (idx === i ? { ...item, status: 'done' as const } : item)));
          setUploadPhase('uploading');
        } catch (err) {
          // Mark current item as error
          setUploadItems((prev) => prev.map((item, idx) => (idx === i ? { ...item, status: 'error' as const } : item)));
          setUploadPhase('error');
          setUploadError(err instanceof Error ? err.message : t('video.upload.errorGeneric'));
          return;
        }
      }

      // All recordings uploaded — submit the interview
      try {
        setUploadPhase('submitting');
        await videoApi.submitInterview(currentInterviewId);
        setUploadPhase('done');
      } catch (err) {
        setUploadPhase('error');
        setUploadError(err instanceof Error ? err.message : t('video.upload.errorSubmit'));
      }
    },
    [t],
  );

  const handleRecordingComplete = useCallback(
    (recordings: Array<{ promptId: string; blob: Blob; durationSec: number }>) => {
      if (!interviewId) {
        return;
      }

      recordingsRef.current = recordings;

      // Build upload items
      const items: UploadItem[] = recordings.map((rec, idx) => ({
        promptId: rec.promptId,
        label: t('video.upload.questionLabel', { current: idx + 1, total: recordings.length }),
        status: 'pending' as const,
      }));

      setUploadItems(items);
      setUploadIndex(0);
      setUploadPercent(0);
      setUploadPhase('uploading');
      setUploadError(null);
      setStep('uploading');

      void runUpload(recordings, 0, interviewId);
    },
    [interviewId, t, runUpload],
  );

  const handleRetry = useCallback(() => {
    if (!interviewId) {
      return;
    }

    setUploadError(null);

    // Find the first non-done item to resume from
    const resumeIndex = uploadItems.findIndex((item) => item.status !== 'done');
    if (resumeIndex >= 0) {
      void runUpload(recordingsRef.current, resumeIndex, interviewId);
      return;
    }

    // All uploads succeeded but submit failed — retry submit only
    const retrySubmit = async () => {
      try {
        setUploadPhase('submitting');
        await videoApi.submitInterview(interviewId);
        setUploadPhase('done');
      } catch (err) {
        setUploadPhase('error');
        setUploadError(err instanceof Error ? err.message : t('video.upload.errorSubmit'));
      }
    };
    void retrySubmit();
  }, [interviewId, uploadItems, runUpload, t]);

  const handleCancel = useCallback(() => {
    void navigate(dashboardUrl);
  }, [navigate, dashboardUrl]);

  const handleDone = useCallback(() => {
    void navigate(dashboardUrl);
  }, [navigate, dashboardUrl]);

  const handleExit = useCallback(() => {
    void navigate(dashboardUrl);
  }, [navigate, dashboardUrl]);

  const handleReRecord = useCallback(() => {
    if (!applicationId) {
      return;
    }

    const startFresh = async () => {
      try {
        const response = await videoApi.startInterview({
          applicationId,
        });
        if (response.data.promptSet) {
          setPrompts(response.data.promptSet.prompts);
          setRetakePolicy(response.data.promptSet.retakePolicy);
        }
        setInterviewId(response.data.videoInterviewId);
        setInterviewSubmittedAt(response.data.submittedAt ?? null);
        setInterviewResponseCount(response.data.responses.length);

        if (response.data.status === 'uploaded') {
          setReRecordError(t('video.alreadyRecorded.reRecordUnavailable'));
          setStep('already-recorded');
          return;
        }

        setReRecordError(null);
        setStep('recording');
      } catch {
        window.alert(t('video.upload.error'));
      }
    };

    void startFresh();
  }, [applicationId, t]);

  const currentStepNumber = STEP_NUMBER[step];

  // Recording step — fullscreen on mobile, wider layout on desktop
  if (step === 'recording') {
    return (
      <div className='fixed inset-0 z-50 bg-gray-950 flex flex-col md:relative md:inset-auto md:z-auto md:bg-transparent md:block md:max-w-4xl md:mx-auto md:p-8'>
        <div className='hidden md:flex mb-6 items-center justify-between'>
          <h1 className='text-2xl font-bold text-gray-900'>{t('video.pageTitle')}</h1>
          <span className='text-sm text-gray-500'>
            {t('video.stepOf', { current: currentStepNumber, total: TOTAL_STEPS })}
          </span>
        </div>
        <div className='flex-1 flex flex-col min-h-0 md:flex-none'>
          <PromptRecorder
            prompts={prompts}
            retakePolicy={retakePolicy}
            onComplete={handleRecordingComplete}
            onExit={handleExit}
          />
        </div>
      </div>
    );
  }

  // Upload step uses wider layout
  if (step === 'uploading') {
    return (
      <div className='max-w-4xl mx-auto p-4 md:p-8'>
        <div className='mb-6 flex items-center justify-between'>
          <h1 className='text-2xl font-bold text-gray-900'>{t('video.pageTitle')}</h1>
          <span className='text-sm text-gray-500'>
            {t('video.stepOf', { current: currentStepNumber, total: TOTAL_STEPS })}
          </span>
        </div>
        <div className='bg-white rounded-xl shadow-sm border border-gray-200 p-6'>
          <UploadProgress
            items={uploadItems}
            currentIndex={uploadIndex}
            uploadPercent={uploadPercent}
            phase={uploadPhase}
            error={uploadError}
            onRetry={handleRetry}
            onCancel={handleCancel}
            onDone={handleDone}
          />
        </div>
      </div>
    );
  }

  // Already-recorded step — shown when API returns an existing completed interview
  if (step === 'already-recorded') {
    return (
      <div className='max-w-3xl mx-auto px-3 py-4 md:p-8'>
        <div className='mb-4 md:mb-8'>
          <button
            type='button'
            onClick={handleBack}
            className='flex items-center text-sm text-gray-500 hover:text-gray-700 mb-3 md:mb-4 transition-colors'>
            <ArrowLeft size={16} className='mr-1' />
            {t('video.backToDashboard')}
          </button>
          <h1 className='text-2xl md:text-3xl font-bold text-gray-900'>{t('video.pageTitle')}</h1>
        </div>

        <div className='bg-white rounded-xl shadow-sm border border-gray-200 p-6 md:p-8 text-center'>
          <div className='flex items-center justify-center w-16 h-16 rounded-full bg-green-100 mx-auto mb-4'>
            <svg
              xmlns='http://www.w3.org/2000/svg'
              className='h-8 w-8 text-green-600'
              fill='none'
              viewBox='0 0 24 24'
              stroke='currentColor'
              strokeWidth={2}
              aria-hidden='true'>
              <path strokeLinecap='round' strokeLinejoin='round' d='M5 13l4 4L19 7' />
            </svg>
          </div>
          <h2 className='text-xl font-bold text-gray-900 mb-2'>{t('video.alreadyRecorded.title')}</h2>
          <p className='text-gray-600 mb-2'>{t('video.alreadyRecorded.message')}</p>
          <div className='mb-6 space-y-1 text-sm text-gray-500'>
            {interviewSubmittedAt ? (
              <p>
                {t('video.alreadyRecorded.recordedAt', {
                  date: new Date(interviewSubmittedAt).toLocaleDateString(),
                })}
              </p>
            ) : null}
            <p>{t('video.alreadyRecorded.responseCount', { count: interviewResponseCount })}</p>
          </div>
          {reRecordError && <p className='text-sm text-red-600 mb-4'>{reRecordError}</p>}
          <button
            type='button'
            onClick={handleReRecord}
            className='inline-flex items-center px-6 py-2.5 bg-white border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 font-medium text-sm transition-colors'>
            {t('video.alreadyRecorded.reRecord')}
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className='max-w-3xl mx-auto px-3 py-4 md:p-8'>
      {/* Header */}
      <div className='mb-4 md:mb-8'>
        <button
          type='button'
          onClick={handleBack}
          className='flex items-center text-sm text-gray-500 hover:text-gray-700 mb-3 md:mb-4 transition-colors'>
          <ArrowLeft size={16} className='mr-1' />
          {t('video.backToDashboard')}
        </button>
        <div className='flex items-center justify-between'>
          <h1 className='text-2xl md:text-3xl font-bold text-gray-900'>{t('video.pageTitle')}</h1>
          <span className='text-xs md:text-sm text-gray-500'>
            {t('video.stepOf', { current: currentStepNumber, total: TOTAL_STEPS })}
          </span>
        </div>
      </div>

      {/* Step content */}
      <div className='bg-white rounded-xl shadow-sm border border-gray-200 p-4 md:p-6'>
        {step === 'consent' ? (
          <VideoConsent onAccept={handleConsentAccepted} />
        ) : step === 'device-check' ? (
          <DeviceCheck onReady={handleDeviceReady} />
        ) : (
          <HowItWorks onStart={handleStartRecording} onExit={handleExit} />
        )}
      </div>
    </div>
  );
}
